/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Which tabs the v2 Account 360 shows, in what order, under which names.
 *
 * Pure so the rules can be tested without rendering the page. A tab appears
 * only when the tenant has the module behind it and, for summary-backed
 * tabs, when the backend returned that section (null means "not bought or
 * not readable"). The vertical (Travel, Real Estate, Higher Ed, Healthcare)
 * never grants a tab; it only renames tabs and moves its own to the front.
 */

/** Apps that are a vertical: their labels and accent dress the page. */
export const VERTICAL_APPS = ["TRAVEL", "REAL_ESTATE", "HIGHER_ED", "HEALTHCARE"];

/**
 * The tenant's vertical app: the active one when it is a vertical, else the
 * tenant's default app when that is one. A Healthcare clinic browsing in the
 * Sales app still sees Patients, not Contacts.
 */
export function resolveVerticalApp(activeApp, apps, defaultAppCode) {
  if (activeApp && VERTICAL_APPS.includes(activeApp.code)) return activeApp;
  const fallback = (apps || []).find((a) => a.code === defaultAppCode);
  return fallback && VERTICAL_APPS.includes(fallback.code) ? fallback : null;
}

const count = (n) => (typeof n === "number" ? n : null);

/**
 * @param {object} p
 * @param {object} [p.labels]      the vertical app's labels ({contacts, deals, cases, ...})
 * @param {string} [p.verticalCode] TRAVEL | REAL_ESTATE | HIGHER_ED | HEALTHCARE | null
 * @param {(code: string) => boolean} p.isModuleEnabled
 * @param {object} p.gates         { showTravel, showLegacyOrders, showMerchant }
 * @param {object} [p.summary]     GET /accounts/{id}/summary (or null)
 * @param {object} [p.travel]      GET /accounts/{id}/travel-summary (or null)
 * @param {object} [p.realEstate]  GET /accounts/{id}/real-estate-summary (or null)
 * @param {object} [p.local]       counts the page already holds, used when the summary is missing
 * @returns {{key: string, label: string, count: number|null, group: string}[]}
 */
export function buildAccount360Tabs({
  labels = {},
  verticalCode = null,
  isModuleEnabled,
  gates = {},
  summary = null,
  travel = null,
  realEstate = null,
  local = {},
}) {
  const c = summary?.counts || {};
  const on = (code) => Boolean(isModuleEnabled && isModuleEnabled(code));
  const name = (key, fallback) => labels[key] || fallback;

  const core = [
    { key: "contacts", label: name("contacts", "Contacts"), count: count(c.contacts ?? local.contacts), group: "core" },
  ];
  if (on("DEALS")) {
    core.push({ key: "deals", label: name("deals", "Deals"), count: count(c.deals_total ?? local.deals), group: "core" });
  }
  if (on("CASES")) {
    core.push({ key: "cases", label: name("cases", "Cases"), count: count(c.cases_total ?? local.cases), group: "core" });
  }
  if (gates.showLegacyOrders && on("ORDERS")) {
    core.push({ key: "orders", label: name("orders", "Orders"), count: count(c.orders ?? local.orders), group: "core" });
  }
  // Travel tenants see their invoices on the Travel invoices tab instead.
  if (!gates.showTravel && on("INVOICES") && (!summary || c.invoices !== null)) {
    core.push({ key: "invoices", label: name("invoices", "Invoices"), count: count(c.invoices), group: "core" });
  }

  const travelTabs = [];
  if (gates.showTravel && travel) {
    if (travel.bookings) {
      travelTabs.push({ key: "bookings", label: name("travel-orders", "Bookings"), count: count(travel.bookings.count), group: "travel" });
    }
    if (travel.travellers) {
      travelTabs.push({ key: "travellers", label: name("travel-contacts", "Travellers"), count: count(travel.travellers.count), group: "travel" });
    }
    if (travel.invoices) {
      travelTabs.push({ key: "travel-invoices", label: "Invoices", count: count(travel.invoices.count), group: "travel" });
    }
  }
  if (gates.showMerchant) {
    const m = travel?.merchant;
    const n = m ? (m.assets?.count || 0) + (m.allocations?.count || 0) : null;
    travelTabs.push({ key: "supplier", label: name("merchants", "Supplier"), count: n, group: "travel" });
  }

  const reTabs = [];
  if (realEstate) {
    if (realEstate.bookings) reTabs.push({ key: "re-bookings", label: "Bookings", count: count(realEstate.bookings.count), group: "realEstate" });
    if (realEstate.viewings) reTabs.push({ key: "viewings", label: "Viewings", count: count(realEstate.viewings.count), group: "realEstate" });
    if (realEstate.leases) reTabs.push({ key: "leases", label: "Leases", count: count(realEstate.leases.count), group: "realEstate" });
    if (realEstate.mortgages) reTabs.push({ key: "mortgages", label: "Mortgages", count: count(realEstate.mortgages.count), group: "realEstate" });
  }

  const tail = [
    { key: "activity", label: "Activity", count: count(c.activities_open), group: "core" },
    { key: "files", label: "Files", count: count(c.attachments ?? local.attachments), group: "core" },
  ];

  const overview = { key: "overview", label: "Overview", count: null, group: "core" };
  // The vertical's own tabs come straight after Overview; any other edition
  // the tenant holds follows the core tabs, so nothing entitled disappears.
  if (verticalCode === "TRAVEL") return [overview, ...travelTabs, ...core, ...reTabs, ...tail];
  if (verticalCode === "REAL_ESTATE") return [overview, ...reTabs, ...core, ...travelTabs, ...tail];
  return [overview, ...core, ...travelTabs, ...reTabs, ...tail];
}
